import React, { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Loader2, User, FileText, Upload, Briefcase } from "lucide-react";
import Swal from "sweetalert2";
import api from "../../../utils/api";

export default function LawyerCreateClientRequest() {
    const [clients, setClients] = useState([]);
    const [caseTypes, setCaseTypes] = useState([]);
    const [currentUser, setCurrentUser] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [errors, setErrors] = useState({});
    const fileInputRef = useRef(null);

    const [formData, setFormData] = useState({
        client_id: "",
        title: "",
        description: "",
        case_type_id: "",
    });

    // Fetch initial data
    useEffect(() => {
        const fetchData = async () => {
            const token = localStorage.getItem("auth_token");
            const authUser = localStorage.getItem("auth_user");

            if (!token || !authUser) {
                Swal.fire("Error", "Authentication required.", "error");
                setLoading(false);
                return;
            }

            try {
                const user = JSON.parse(authUser);
                if (!user.id) {
                    Swal.fire("Error", "Lawyer ID not found.", "error");
                    setLoading(false);
                    return;
                }

                setCurrentUser(user);

                const [clientsRes, caseTypesRes] = await Promise.all([
                    api.get("/clients", { headers: { Authorization: `Bearer ${token}` } }),
                    api.get(`/lawyers/${user.id}/case-types`, { headers: { Authorization: `Bearer ${token}` } }),
                ]);

                if (clientsRes.data.success) setClients(clientsRes.data.data || []);
                if (caseTypesRes.data.success) {
                    setCaseTypes((caseTypesRes.data.data || []).filter(ct => ct.is_active));
                }
            } catch (err) {
                Swal.fire("Error", err.response?.data?.message || "Failed to load data.", "error");
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, []);

    // Handle form submission
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!currentUser?.id) return;

        setSaving(true);
        setErrors({});

        const token = localStorage.getItem("auth_token");
        const payload = new FormData();

        payload.append("client_id", formData.client_id);
        payload.append("lawyer_id", currentUser.id);
        payload.append("title", formData.title.trim());
        payload.append("description", formData.description.trim());
        payload.append("case_type_id", formData.case_type_id);

        // Attach files (same as working OfficeCreateClientRequest)
        const files = fileInputRef.current?.files;
        if (files && files.length > 0) {
            for (let i = 0; i < files.length; i++) {
                payload.append("files[]", files[i]);
            }
        }

        try {
            const res = await api.post("/requests/for-client", payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "multipart/form-data", // Required for file upload
                },
            });

            if (res.data.success) {
                Swal.fire({
                    icon: "success",
                    title: "Request Created!",
                    html: `<p class="text-sm">Request #<strong>${res.data.data.id}</strong> has been sent successfully.</p>`,
                    timer: 4000,
                    timerProgressBar: true,
                });

                setFormData({ client_id: "", title: "", description: "", case_type_id: "" });
                if (fileInputRef.current) fileInputRef.current.value = "";
                setErrors({});
            }
        } catch (err) {
            console.error("Validation Error:", err.response?.data);

            const apiErrors = err.response?.data?.errors || {};
            const fieldErrors = {};

            Object.keys(apiErrors).forEach((key) => {
                fieldErrors[key.startsWith("files") ? "files" : key] = apiErrors[key][0];
            });

            setErrors(fieldErrors);

            let errorText = "Validation failed:\n\n";
            Object.keys(fieldErrors).forEach((key) => {
                const label = {
                    client_id: "Client",
                    lawyer_id: "Lawyer",
                    title: "Title",
                    description: "Description",
                    case_type_id: "Case Type",
                    files: "Files",
                }[key] || key;
                errorText += `• ${label}: ${fieldErrors[key]}\n`;
            });

            Swal.fire({
                icon: "error",
                title: "Validation Failed",
                text: errorText,
                confirmButtonColor: "#d33",
                width: "600px",
            });
        } finally {
            setSaving(false);
        }
    };

    // Loading state
    if (loading) {
        return (
            <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
            </div>
        );
    }

    if (!currentUser) {
        return (
            <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
                <p className="text-red-700 font-medium">User not authenticated.</p>
            </div>
        );
    }

    return (
        <section className="p-6 min-h-screen">
            <div className="max-w-4xl mx-auto">
                <div className="mb-8">
                    <h2 className="text-2xl font-bold text-gray-900">Create Client Request</h2>
                    <p className="text-gray-600 mt-1">Submit a consultation request on behalf of a client</p>
                    <p className="text-sm text-blue-600 mt-2 flex items-center gap-2">
                        <Briefcase className="w-4 h-4" />
                        Submitting as: <strong>{currentUser.name}</strong> (ID: {currentUser.id})
                    </p>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="bg-white rounded-xl shadow-sm border border-gray-200 p-6"
                >
                    <form onSubmit={handleSubmit} className="space-y-6">

                        {/* Client */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <User className="w-4 h-4 inline mr-1" />
                                Select Client *
                            </label>
                            <select
                                required
                                value={formData.client_id}
                                onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.client_id ? "border-red-500" : "border-gray-300"
                                    }`}
                            >
                                <option value="">Choose a client...</option>
                                {clients.map((client) => (
                                    <option key={client.id} value={client.id}>
                                        {client.name} ({client.email})
                                    </option>
                                ))}
                            </select>
                            {errors.client_id && <p className="mt-1 text-sm text-red-600">{errors.client_id}</p>}
                        </div>

                        {/* Case Type */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <FileText className="w-4 h-4 inline mr-1" />
                                Case Type *
                            </label>
                            <select
                                required
                                value={formData.case_type_id}
                                onChange={(e) => setFormData({ ...formData, case_type_id: e.target.value })}
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.case_type_id ? "border-red-500" : "border-gray-300"
                                    }`}
                            >
                                <option value="">Choose a case type...</option>
                                {caseTypes.map((ct) => (
                                    <option key={ct.id} value={ct.id}>
                                        {ct.name} - ${parseFloat(ct.price).toFixed(2)}
                                    </option>
                                ))}
                            </select>
                            {errors.case_type_id && <p className="mt-1 text-sm text-red-600">{errors.case_type_id}</p>}
                        </div>

                        {/* Title */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Request Title *</label>
                            <input
                                type="text"
                                required
                                value={formData.title}
                                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                                placeholder="e.g., Divorce Consultation"
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${errors.title ? "border-red-500" : "border-gray-300"
                                    }`}
                            />
                            {errors.title && <p className="mt-1 text-sm text-red-600">{errors.title}</p>}
                        </div>

                        {/* Description */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">Description *</label>
                            <textarea
                                required
                                rows={4}
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                                placeholder="Provide details about the case..."
                                className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none ${errors.description ? "border-red-500" : "border-gray-300"
                                    }`}
                            />
                            {errors.description && <p className="mt-1 text-sm text-red-600">{errors.description}</p>}
                        </div>

                        {/* Files */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                <Upload className="w-4 h-4 inline mr-1" />
                                Attach Files (Optional)
                            </label>
                            <input
                                type="file"
                                multiple
                                ref={fileInputRef}
                                className={`w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100 ${errors.files ? "border-red-500 border" : ""
                                    }`}
                            />
                            <p className="text-xs text-gray-500 mt-1">You can upload multiple documents (PDF, images, etc.)</p>
                            {errors.files && <p className="mt-1 text-sm text-red-600">{errors.files}</p>}
                        </div>

                        {/* Buttons */}
                        <div className="flex justify-end gap-3 pt-4">
                            <button
                                type="button"
                                onClick={() => {
                                    setFormData({ client_id: "", title: "", description: "", case_type_id: "" });
                                    if (fileInputRef.current) fileInputRef.current.value = "";
                                    setErrors({});
                                }}
                                className="px-5 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition"
                            >
                                Clear
                            </button>
                            <button
                                type="submit"
                                disabled={saving}
                                className={`px-6 py-2 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition flex items-center gap-2 disabled:opacity-70 disabled:cursor-not-allowed`}
                            >
                                {saving ? (
                                    <>
                                        <Loader2 className="w-4 h-4 animate-spin" />
                                        Creating...
                                    </>
                                ) : (
                                    <>
                                        <FileText className="w-4 h-4" />
                                        Create Request
                                    </>
                                )}
                            </button>
                        </div>
                    </form>
                </motion.div>
            </div>
        </section>
    );
}